/**
 * WebSocket client for board real-time updates
 */

export interface WebSocketMessage {
  event: string;
  data: any;
}

export type WebSocketEventHandler = (data: any) => void;

export class BoardWebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private token: string;
  private getToken: () => Promise<string>;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private eventHandlers: Map<string, Set<WebSocketEventHandler>> = new Map();
  private subscribedBoards: Set<string> = new Set();
  private isConnecting = false;
  private isRefreshingToken = false;

  constructor(url: string, token: string, getToken: () => Promise<string>) {
    this.url = url;
    this.token = token;
    this.getToken = getToken;
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return Promise.resolve();
    }

    this.isConnecting = true;

    // Refresh token if needed before connecting
    if (this.isRefreshingToken) {
      // Wait for token refresh to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      return this.connect();
    }

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = `${this.url}?token=${this.token}`;
        const ws = new WebSocket(wsUrl);
        this.ws = ws;

        ws.onopen = () => {
          // If this socket is no longer the active one, close it immediately
          if (this.ws !== ws) {
            ws.close();
            return;
          }

          console.log('[WebSocket] Connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;

          // Resubscribe to boards after reconnection
          this.subscribedBoards.forEach(boardId => {
            this.subscribeToBoard(boardId);
          });

          resolve();
        };

        ws.onmessage = (event) => {
          if (this.ws !== ws) return;
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('[WebSocket] Failed to parse message:', error);
          }
        };

        ws.onerror = (error) => {
          if (this.ws !== ws) return;
          console.error('[WebSocket] Error:', error);
          this.isConnecting = false;
          reject(error);
        };

        ws.onclose = (event) => {
          if (this.ws !== ws) return;

          console.log('[WebSocket] Disconnected', event.code, event.reason);
          this.isConnecting = false;
          this.ws = null;

          // Don't reconnect on normal closure
          if (event.code === 1000) {
            return;
          }

          // Check if disconnection was due to token expiration
          if (event.code === 4001 || event.reason.includes('jwt expired') || event.reason.includes('Authentication failed')) {
            console.log('[WebSocket] Token expired, attempting refresh and reconnect');
            this.handleTokenExpiration()
              .then(() => {
                this.attemptReconnect();
              })
              .catch(error => {
                console.error('[WebSocket] Token refresh failed, stopping reconnection:', error);
                // Don't attemptReconnect here to avoid loops with expired tokens
                this.isConnecting = false;
              });
          } else {
            this.attemptReconnect();
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private async handleTokenExpiration(): Promise<void> {
    if (this.isRefreshingToken) {
      return; // Already refreshing
    }

    this.isRefreshingToken = true;
    try {
      console.log('[WebSocket] Refreshing expired token...');
      const newToken = await this.getToken();
      this.token = newToken;
      console.log('[WebSocket] Token refreshed successfully');
    } catch (error) {
      console.error('[WebSocket] Failed to refresh token:', error);
      throw error;
    } finally {
      this.isRefreshingToken = false;
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch(error => {
        console.error('[WebSocket] Reconnection failed:', error);
      });
    }, delay);
  }

  /**
   * Update the token used for WebSocket connections
   */
  updateToken(newToken: string): void {
    if (this.token === newToken) {
      return;
    }

    this.token = newToken;

    // If connected, we need to reconnect with the new token
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Token updated, reconnecting...');
      this.disconnect();
      this.connect().catch(error => {
        console.error('[WebSocket] Reconnection after token update failed:', error);
      });
    }
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000); // 1000 = Normal Closure
      this.ws = null;
    }

    this.subscribedBoards.clear();
    this.reconnectAttempts = 0;
  }

  subscribeToBoard(boardId: string) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Will resubscribe when connection is established
      this.subscribedBoards.add(boardId);
      return;
    }

    this.subscribedBoards.add(boardId);
    this.send({
      event: 'board:subscribe',
      data: { boardId }
    });
  }

  unsubscribeFromBoard(boardId: string) {
    this.subscribedBoards.delete(boardId);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({
        event: 'board:unsubscribe',
        data: { boardId }
      });
    }
  }

  on(event: string, handler: WebSocketEventHandler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: WebSocketEventHandler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private send(message: WebSocketMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send message, not connected');
      return;
    }

    this.ws.send(JSON.stringify(message));
  }

  private handleMessage(message: WebSocketMessage) {
    console.log('[WebSocket] Received:', message);

    const handlers = this.eventHandlers.get(message.event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message.data);
        } catch (error) {
          console.error('[WebSocket] Error in event handler:', error);
        }
      });
    }
  }
}

// Singleton instance
let wsClient: BoardWebSocketClient | null = null;

export function getWebSocketClient(token?: string, getToken?: () => Promise<string>): BoardWebSocketClient {
  // Get runtime config injected by the server
  const config = (typeof window !== 'undefined' && (window as any).__DASHBAL_CONFIG__) || {};

  // Get WebSocket URL from runtime config
  // In production: wss://api.dashbal.com/ws
  // In development: ws://localhost:3001/ws
  let wsUrl = config.wsUrl;

  if (!wsUrl) {
    if (process.env.API_BASE_URL) {
      // Attempt to derive from API URL
      try {
        const apiUrl = new URL(process.env.API_BASE_URL);
        apiUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        apiUrl.pathname = apiUrl.pathname === '/' ? '/ws' : `${apiUrl.pathname}/ws`.replace('//', '/');
        wsUrl = apiUrl.toString();
      } catch (e) {
        // Invalid URL, ignore
      }
    }

    if (!wsUrl) {
      if (process.env.NODE_ENV !== 'production') {
        wsUrl = 'ws://localhost:3001/ws';
      } else {
        // Production fallback - assume same host, protocol upgrade
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host; // e.g. api.dashbal.com
        console.warn('WS URL not configured. Defaulting to safe fallback.');
        wsUrl = `${protocol}//${host}/ws`;
      }
    }
  }

  if (!wsClient && token && getToken) {
    wsClient = new BoardWebSocketClient(wsUrl, token, getToken);
  }

  if (!wsClient) {
    throw new Error('WebSocket client not initialized. Please provide a token and token refresh function.');
  }

  // Update token if provided and client already exists
  if (wsClient && token) {
    wsClient.updateToken(token);
  }

  return wsClient;
}

export function disconnectWebSocket() {
  if (wsClient) {
    wsClient.disconnect();
    wsClient = null;
  }
}
